import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, requestJson } from './client';
import { authHeaders } from './regulations';

export type Currency = 'RUB' | 'AMD';
export type DeductionKind = 'late' | 'early' | 'absent';

export interface Salary {
  amountMinor: number;
  currency: Currency;
  /** Премия за KPI сверх оклада, % от оклада. */
  kpiPercent?: number | null;
  effectiveFrom: string;
}

/** KPI за месяц: период, выполнение, процент премии и сама премия. */
export interface MonthKpi {
  period: { from: string; to: string };
  total: number | null;
  percent: number | null;
  premiumMinor: number | null;
}

export interface Deduction {
  date: string;
  kind: DeductionKind;
  minutes: number | null;
  amountMinor: number;
  currency: Currency;
  /** Дневная ставка — только для тех, кто видит оклад. */
  dailyRateMinor: number | null;
  cancelled: { reason: string; by: string | null; at: string } | null;
}

export interface DeductionTotals {
  deductedMinor: number;
  cancelledMinor: number;
  late: number;
  early: number;
  absent: number;
}

export interface ExcusedAbsence {
  id: string;
  dateFrom: string;
  dateTo: string;
  reason: string;
  createdBy: string | null;
}

export interface EmployeePayroll {
  month: string;
  from: string;
  to: string;
  today: string;
  linked: boolean;
  schedule: { workStart: string | null; lateAfter: string | null; workEnd?: string | null } | null;
  hasSalary: boolean;
  canSeeSalary: boolean;
  canManage: boolean;
  canExcuse: boolean;
  salary: {
    current: Salary | null;
    dailyRateMinor: number | null;
    accrual: { amountMinor: number; currency: Currency } | null;
    kpi: MonthKpi | null;
    kpiPercent: number | null;
    /** Нарушения из файла за месяц; null — нарушения этому пользователю не видны. */
    finesMinor: number | null;
    /** Итог месяца: оклад + премия за KPI − удержания − нарушения. */
    totalMinor: number | null;
    history: (Salary & { updatedAt: string; updatedBy: string | null })[];
  } | null;
  totals: DeductionTotals & { currency: Currency | null };
  items: Deduction[];
  excused: ExcusedAbsence[];
  excusedDays: string[];
  holidays: { day: string; title: string }[];
}

export interface PayrollPerson {
  id: string;
  fullName: string;
  position: string | null;
  city: string | null;
  photoUrl: string | null;
  bitrixId: string | null;
  linked: boolean;
  hasSchedule: boolean;
  canEdit: boolean;
  salary: Salary | null;
  accrual: { amountMinor: number; currency: Currency } | null;
  kpi: MonthKpi | null;
  totals: DeductionTotals;
  /** Нарушения из файла за месяц; null — не видны. */
  finesMinor: number | null;
  /** Итог месяца: оклад + премия за KPI − удержания − нарушения. */
  totalMinor: number | null;
}

export interface PayrollList {
  month: string;
  mode: 'none' | 'list';
  canManage: boolean;
  people: PayrollPerson[];
  holidays: { day: string; title: string }[];
}

export const DEDUCTION_LABELS: Record<DeductionKind, string> = {
  late: 'Опоздание',
  early: 'Ранний уход',
  absent: 'Прогул',
};

const CURRENCY_SIGN: Record<Currency, string> = { RUB: '₽', AMD: '֏' };
const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });
export const formatMoney = (minor: number, currency: Currency | null) =>
  `${money.format(minor / 100)}${currency ? ` ${CURRENCY_SIGN[currency]}` : ''}`;

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
export const monthLabel = (month: string) =>
  `${MONTHS[Number(month.slice(5, 7)) - 1] ?? ''} ${month.slice(0, 4)}`;

/** Текущий месяц по Москве, ГГГГ-ММ. */
export const moscowMonth = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(now).slice(0, 7);

/** Текущий и два предыдущих месяца: за них и смотрят удержания. */
export function recentMonths(current: string, count = 3) {
  const [year = 0, index = 1] = current.split('-').map(Number);
  return Array.from({ length: count }, (_, shift) => {
    const date = new Date(Date.UTC(year, index - 1 - shift, 1));
    return date.toISOString().slice(0, 7);
  });
}

export function useEmployeePayroll(employeeId: string, month: string | null) {
  return useQuery({
    queryKey: ['payroll', employeeId, month],
    queryFn: () =>
      requestJson<EmployeePayroll>(
        `/api/v1/employees/${employeeId}/payroll${month ? `?month=${month}` : ''}`,
      ),
  });
}

export function usePayrollList(month: string | null) {
  return useQuery({
    queryKey: ['payroll-list', month],
    queryFn: () => requestJson<PayrollList>(`/api/v1/payroll${month ? `?month=${month}` : ''}`),
  });
}

/** Любое изменение оклада, удержания или отсутствия обновляет и кабинет, и список. */
function usePayrollMutation<T>(run: (input: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all(
        ['payroll', 'payroll-list'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
      ),
  });
}

export const useSaveSalary = (employeeId: string) =>
  usePayrollMutation(
    (input: { amount: number; currency: Currency; kpiPercent: number | null; effectiveFrom: string }) =>
      requestJson(`/api/v1/employees/${employeeId}/salary`, { method: 'PUT', body: JSON.stringify(input) }),
  );

export const useDeleteSalary = (employeeId: string) =>
  usePayrollMutation((effectiveFrom: string) =>
    requestJson(`/api/v1/employees/${employeeId}/salary/${effectiveFrom}`, { method: 'DELETE' }),
  );

export const useCancelDeduction = (employeeId: string) =>
  usePayrollMutation((input: { date: string; kind: DeductionKind; reason: string }) =>
    requestJson(`/api/v1/employees/${employeeId}/deductions/cancel`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );

export const useRestoreDeduction = (employeeId: string) =>
  usePayrollMutation((input: { date: string; kind: DeductionKind }) =>
    requestJson(`/api/v1/employees/${employeeId}/deductions/${input.date}/${input.kind}`, {
      method: 'DELETE',
    }),
  );

export const useAddAbsence = (employeeId: string) =>
  usePayrollMutation((input: { from: string; to: string; reason: string }) =>
    requestJson(`/api/v1/employees/${employeeId}/absences`, { method: 'POST', body: JSON.stringify(input) }),
  );

export const useDeleteAbsence = (employeeId: string) =>
  usePayrollMutation((absenceId: string) =>
    requestJson(`/api/v1/employees/${employeeId}/absences/${absenceId}`, { method: 'DELETE' }),
  );

export const useSaveHolidays = () =>
  usePayrollMutation((items: { day: string; title: string }[]) =>
    requestJson('/api/v1/payroll/holidays', { method: 'PUT', body: JSON.stringify({ items }) }),
  );

export interface SalaryImportResult {
  saved: number;
  errors: { row: number; message: string }[];
}

/** Оклады файлом: bitrix_user_id, оклад, валюта, KPI %, с даты. */
export async function uploadSalaries(file: File, effectiveFrom: string): Promise<SalaryImportResult> {
  const response = await fetch(`/api/v1/payroll/salaries/import?effectiveFrom=${effectiveFrom}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'x-goodway-csrf': '1',
      ...authHeaders(),
    },
    body: file,
  });
  const body = (await response.json().catch(() => null)) as
    | (SalaryImportResult & { error?: { message?: string } })
    | null;
  if (!response.ok)
    throw new ApiError(response.status, 'BAD_REQUEST', body?.error?.message ?? 'Файл не загружен');
  return body as SalaryImportResult;
}
